import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, LayoutGrid, List, Plus, Search } from 'lucide-react'
import { searchDocs } from '../utils/docSearch'
import { useCreatePage } from '../hooks/useCreatePage'
import { DocsOutlineTable } from './DocsOutlineTable'
import { DocsSearchPanel } from './DocsSearchPanel'
import type { DocSearchResult } from '../utils/docSearch'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { usePageContents, useProjectPages } from '@/lib/db/rxdb/hooks'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { extractPlainTextFromTiptap } from '@/routes/features/tasks/utils/editorUtils'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { cn } from '@/lib/utils'

type DocsView = 'grid' | 'list'
const VIEW_KEY = 'moly-docs-index-view'

function readView(): DocsView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'
  } catch {
    return 'grid'
  }
}

interface DocsIndexProps {
  projectId: string
  projectKey: string
  workspaceSlug: string
  navigateToPage?: (slug: string) => void
}

/**
 * A space's or project's docs: cards with a preview of each top-level doc
 * (design 3a), or the outline table for reorganising (4c). Search covers
 * titles and bodies and shows each hit's full path.
 */
export function DocsIndex({ projectId, projectKey, workspaceSlug, navigateToPage }: DocsIndexProps) {
  const { t } = useTranslation('projects')
  const [view, setView] = useState<DocsView>(readView)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [revealId, setRevealId] = useState<string | null>(null)
  const { pages } = useProjectPages(projectId)
  const { contents } = usePageContents()
  const createPage = useCreatePage({ projectId, projectKey, workspaceSlug, navigateToPage })

  const live = useMemo(() => (pages ?? []).filter((p) => !p.isDeleted), [pages])
  const topLevel = useMemo(
    () =>
      live
        .filter((p) => !p.parentPageId)
        .sort((a, b) => (a.sortOrder ?? '').localeCompare(b.sortOrder ?? '') || a.createdOn - b.createdOn),
    [live],
  )
  const previewByPage = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of contents) map.set(c.pageId, (c.plainText ?? extractPlainTextFromTiptap(c.content)).trim())
    return map
  }, [contents])
  const childCount = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of live) if (p.parentPageId) map.set(p.parentPageId, (map.get(p.parentPageId) ?? 0) + 1)
    return map
  }, [live])

  const results = useMemo(
    () => searchDocs(live, previewByPage, query, t('docs.untitled')),
    [live, previewByPage, query, t],
  )
  const searching = query.trim().length > 0

  const openPage = (slug: string) => navigateToPage?.(slug)
  const reveal = (result: DocSearchResult) => {
    changeView('list')
    setQuery('')
    // A fresh value each time, so revealing the same doc twice still scrolls.
    setRevealId(null)
    requestAnimationFrame(() => setRevealId(result.page.id))
  }

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('')
      return
    }
    if (!results.length) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((i) => (i + step + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const result = results[Math.min(activeIndex, results.length - 1)]
      if (!result) return
      if (e.metaKey || e.ctrlKey) reveal(result)
      else openPage(result.page.slug)
    }
  }

  const changeView = (next: DocsView) => {
    setView(next)
    try {
      localStorage.setItem(VIEW_KEY, next)
    } catch {
      // Preference only; fine to lose.
    }
  }

  const panel = searching ? (
    <DocsSearchPanel
      query={query}
      results={results}
      activeIndex={Math.min(activeIndex, Math.max(results.length - 1, 0))}
      onHover={setActiveIndex}
      onOpen={(result) => openPage(result.page.slug)}
      onReveal={reveal}
      className={view === 'list' ? 'lg:sticky lg:top-0' : undefined}
    />
  ) : null

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <p className="mr-auto text-sm text-muted-foreground">{t('docs.count', { count: live.length })}</p>
        <label className="flex h-8 w-full items-center gap-2 rounded-lg border bg-card px-2.5 focus-within:border-foreground sm:w-60">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={onSearchKeyDown}
            placeholder={t('docs.search.placeholder')}
            aria-label={t('docs.search.placeholder')}
            role="combobox"
            aria-expanded={searching}
            aria-controls="docs-search-results"
            aria-activedescendant={searching && results[activeIndex] ? `docs-search-${results[activeIndex].page.id}` : undefined}
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-[13px] [&::-webkit-search-cancel-button]:hidden"
          />
        </label>
        <div role="radiogroup" aria-label={t('docs.viewAs')} className="flex items-center gap-0.5 rounded-lg bg-secondary p-0.5">
          {(['grid', 'list'] as const).map((option) => {
            const Icon = option === 'grid' ? LayoutGrid : List
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={view === option}
                onClick={() => changeView(option)}
                className={cn(
                  'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
                  view === option ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="size-3.5" />
                {t(`docs.${option}`)}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => void createPage()}
          className="flex h-8 items-center gap-1.5 rounded-lg bg-foreground px-3 text-[13px] font-medium text-background transition-opacity hover:opacity-90"
        >
          <Plus className="size-3.5" />
          {t('docs.newDoc')}
        </button>
      </div>

      {view === 'list' ? (
        <div className={cn('grid gap-5', searching && 'lg:grid-cols-[minmax(0,1fr)_340px]')}>
          {searching && <div className="lg:order-2">{panel}</div>}
          <DocsOutlineTable
            pages={live}
            revealId={revealId}
            onOpen={(page) => openPage(page.slug)}
            onCreatePage={(parentId) => void createPage(parentId)}
          />
        </div>
      ) : searching ? (
        <div className="max-w-xl">{panel}</div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {topLevel.map((page) => (
            <li key={page.id}>
              <DocCard
                page={page}
                preview={previewByPage.get(page.id) ?? ''}
                subpages={childCount.get(page.id) ?? 0}
                onOpen={() => openPage(page.slug)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function DocCard({
  page,
  preview,
  subpages,
  onOpen,
}: {
  page: PageDocument
  preview: string
  subpages: number
  onOpen: () => void
}) {
  const { t } = useTranslation('projects')
  const { date: formatDate } = useDateTimeFormatters()
  const lines = preview.split('\n').filter((l) => l.trim()).slice(0, 4)

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block w-full overflow-hidden rounded-xl border bg-card text-left transition-colors hover:border-input"
    >
      {/* Preview: a paper sheet with the doc's first lines */}
      <div className="relative h-[132px] overflow-hidden bg-secondary/60 px-6 pt-5">
        <div className="h-full rounded-t-md bg-card px-4 pt-3.5 shadow-sm">
          <p className="mb-2 truncate text-[11px] font-semibold">{page.title || t('docs.untitled')}</p>
          {lines.length > 0 ? (
            lines.map((line, i) => (
              <p key={i} className="mb-1 truncate text-[9.5px] leading-snug text-muted-foreground">
                {line}
              </p>
            ))
          ) : (
            <p className="text-[9.5px] italic text-muted-foreground">{t('docs.emptyPreview')}</p>
          )}
        </div>
      </div>
      <div className="flex items-start gap-2 px-4 pb-3.5 pt-3">
        {page.icon ? (
          <ProjectIcon iconType={page.iconType} icon={page.icon} iconColor={page.iconColor} size={16} />
        ) : (
          <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0">
          <span className="block truncate text-[14px] font-semibold">{page.title || t('docs.untitled')}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {[
              subpages > 0 ? t('docs.subpages', { count: subpages }) : null,
              t('docs.edited', { date: formatDate(new Date(page.updatedOn), { month: 'short', day: 'numeric' }) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      </div>
    </button>
  )
}
